import type { ReactNode } from 'react';

/**
 * Supported social platforms. The id is what we store as the key inside
 * `profiles.social_links`. Adding a new platform = appending one entry
 * here; no schema change needed.
 *
 * Icons are the official brand glyphs in their real colours (team
 * request — the platforms should read as themselves). Website has no
 * brand mark, so it stays a neutral monoline globe on currentColor.
 */

export type SocialPlatformId =
  | 'instagram'
  | 'tiktok'
  | 'youtube'
  | 'pinterest'
  | 'substack'
  | 'website';

export interface SocialPlatform {
  id: SocialPlatformId;
  label: string;
  /** Hint shown next to the input in the edit form. */
  placeholder: string;
  /** Fixed prefix drawn inside the edit input ('@' for handle platforms). */
  prefix?: string;
  /** Strip URL chrome / leading @ so we store a canonical handle. */
  sanitize: (raw: string) => string;
  /** Build the outbound URL from a sanitized stored value. */
  urlFor: (value: string) => string;
  /** Inline SVG (24x24 viewBox). */
  Icon: () => ReactNode;
}

// ---------- Icons (official brand glyphs, 24x24, brand colours) ----------

const BrandSvg = ({ children }: { children: ReactNode }) => (
  <svg
    viewBox="0 0 24 24"
    width="100%"
    height="100%"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

const InstagramIcon = () => (
  <BrandSvg>
    <defs>
      {/* The familiar IG sunset — radial from the bottom-left corner. */}
      <radialGradient id="cc-ig-grad" cx="30%" cy="107%" r="150%">
        <stop offset="0%" stopColor="#FDF497" />
        <stop offset="9%" stopColor="#FDF497" />
        <stop offset="45%" stopColor="#FD5949" />
        <stop offset="60%" stopColor="#D6249F" />
        <stop offset="90%" stopColor="#285AEB" />
      </radialGradient>
    </defs>
    <path
      fill="url(#cc-ig-grad)"
      d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"
    />
  </BrandSvg>
);

/* TikTok's note glyph, drawn as the official layered mark: cyan + red
   offset shadows under the black note. */
const TIKTOK_NOTE =
  'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z';
const TikTokIcon = () => (
  <BrandSvg>
    <path fill="#25F4EE" transform="translate(-0.6 -0.6)" d={TIKTOK_NOTE} />
    <path fill="#FE2C55" transform="translate(0.6 0.6)" d={TIKTOK_NOTE} />
    <path fill="#000000" d={TIKTOK_NOTE} />
  </BrandSvg>
);

const YouTubeIcon = () => (
  <BrandSvg>
    <path
      fill="#FF0000"
      d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"
    />
    <path fill="#FFFFFF" d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </BrandSvg>
);

const PinterestIcon = () => (
  <BrandSvg>
    <path
      fill="#E60023"
      d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"
    />
  </BrandSvg>
);

const SubstackIcon = () => (
  <BrandSvg>
    <path
      fill="#FF6719"
      d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"
    />
  </BrandSvg>
);

/* No brand mark for a personal site — neutral monoline globe on
   currentColor, as before. */
const WebsiteIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="100%"
    height="100%"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3c2.8 3 4.2 6 4.2 9s-1.4 6-4.2 9c-2.8-3-4.2-6-4.2-9s1.4-6 4.2-9z" />
  </svg>
);

// ---------- Platforms ------------------------------------------------

/** Strip a leading @ and any leading/trailing whitespace. */
const cleanHandle = (raw: string) => raw.trim().replace(/^@+/, '');

/** Strip protocol + a known host prefix and any trailing slash. */
function unprefix(raw: string, hosts: string[]): string {
  let v = raw.trim();
  for (const host of hosts) {
    const re = new RegExp(`^https?://(www\\.)?${host.replace(/\./g, '\\.')}/?@?`, 'i');
    v = v.replace(re, '');
  }
  return v.replace(/\/+$/, '');
}

/** Ensure a value parses as an http(s) URL. Otherwise prepend https://. */
function ensureUrl(raw: string): string {
  const v = raw.trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v.replace(/^\/+/, '')}`;
}

export const SOCIAL_PLATFORMS: SocialPlatform[] = [
  {
    id: 'instagram',
    label: 'Instagram',
    placeholder: 'your handle',
    prefix: '@',
    sanitize: (raw) => cleanHandle(unprefix(raw, ['instagram.com'])),
    urlFor: (v) => `https://instagram.com/${cleanHandle(v)}`,
    Icon: InstagramIcon,
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    placeholder: 'your handle',
    prefix: '@',
    sanitize: (raw) => cleanHandle(unprefix(raw, ['tiktok.com'])),
    urlFor: (v) => `https://tiktok.com/@${cleanHandle(v)}`,
    Icon: TikTokIcon,
  },
  {
    id: 'youtube',
    label: 'YouTube',
    placeholder: 'your handle',
    prefix: '@',
    sanitize: (raw) => {
      const trimmed = raw.trim();
      // Channel handle (@…) or a full URL — keep URLs intact, otherwise
      // canonicalise to a handle so we can build /@handle below.
      if (/^https?:\/\//i.test(trimmed)) return trimmed.replace(/\/+$/, '');
      return cleanHandle(trimmed);
    },
    urlFor: (v) => (/^https?:\/\//i.test(v) ? v : `https://youtube.com/@${cleanHandle(v)}`),
    Icon: YouTubeIcon,
  },
  {
    id: 'pinterest',
    label: 'Pinterest',
    placeholder: 'your handle',
    prefix: '@',
    sanitize: (raw) => cleanHandle(unprefix(raw, ['pinterest.com', 'pinterest.co.uk', 'pin.it'])),
    urlFor: (v) => `https://pinterest.com/${cleanHandle(v)}`,
    Icon: PinterestIcon,
  },
  {
    id: 'substack',
    label: 'Substack',
    placeholder: 'your link',
    // Substack varies (subdomain or custom domain), so we store and emit
    // a full URL rather than a handle.
    sanitize: (raw) => ensureUrl(raw),
    urlFor: (v) => ensureUrl(v),
    Icon: SubstackIcon,
  },
  {
    id: 'website',
    label: 'Website',
    placeholder: 'your link',
    sanitize: (raw) => ensureUrl(raw),
    urlFor: (v) => ensureUrl(v),
    Icon: WebsiteIcon,
  },
];

/** Lookup by id. Returns undefined for unknown ids (e.g. an older record
 *  with a platform we've since removed); callers should skip those. */
export function getPlatform(id: string): SocialPlatform | undefined {
  return SOCIAL_PLATFORMS.find((p) => p.id === id);
}

/**
 * Read a profile's social_links + the legacy instagram_handle column and
 * return an ordered list of [platform, value] pairs to render. Order is
 * the SOCIAL_PLATFORMS order so the icon row stays stable across visits.
 *
 * The instagram_handle fallback exists for profiles that pre-date the
 * social_links backfill or that have it cleared somehow — the column
 * itself is not authoritative going forward.
 */
export function resolveSocialEntries(
  socialLinks: Record<string, string> | null | undefined,
  legacyInstagramHandle: string | null | undefined,
): Array<{ platform: SocialPlatform; value: string }> {
  const map: Record<string, string> = { ...(socialLinks ?? {}) };
  if (!map.instagram && legacyInstagramHandle && legacyInstagramHandle.trim()) {
    map.instagram = legacyInstagramHandle.trim().replace(/^@+/, '');
  }
  const entries: Array<{ platform: SocialPlatform; value: string }> = [];
  for (const platform of SOCIAL_PLATFORMS) {
    const raw = map[platform.id];
    if (raw && typeof raw === 'string' && raw.trim()) {
      entries.push({ platform, value: raw });
    }
  }
  return entries;
}
